import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { AppState } from "..";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { Product } from "@/models/product";

export interface FaceAnalysisData {
  face_shape: {
    confidence: number;
    label: string;
  };
  image_name: string;
  success: boolean;
  tone: string;
  undertone: string;
}

export interface JewelryRecommendation {
  length?: string[];
  metal?: string[];
  shape?: string[];
  stonecolour?: string[];
  style?: string[];
  type?: string[];
}

export interface RecommendationsData {
  Earring?: JewelryRecommendation;
  Necklace?: JewelryRecommendation;
  complexion?: string;
  face_shape?: string;
  success?: boolean;
  undertone?: string | null;
}

export interface ShapeOfYouState {
  faceAnalysis: FaceAnalysisData | null;
  recommendations: RecommendationsData | null;
  capturedImage: string | null;
  virtualTryOnImage: string | null;
  product: Product | null;
  selectedPrice: Product["prices"][0] | null;
  media: any | null;
}

const initialState: ShapeOfYouState = {
  faceAnalysis: null,
  recommendations: null,
  capturedImage: null,
  virtualTryOnImage: null,
  product: null,
  selectedPrice: null,
  media: null,
};

export const fetchProductForShapeOfYou = createAsyncThunk(
  "shapeOfYou/fetchProductForShapeOfYou",
  async (slug: string) => {
    const { data } = await httpClient.get(endpoints.products.product(slug));
    return data;
  }
);

export const shapeOfYouSlice = createSlice({
  name: "shapeOfYou",
  initialState,
  reducers: {
    setFaceAnalysis: (
      state,
      action: PayloadAction<ShapeOfYouState["faceAnalysis"]>
    ) => {
      state.faceAnalysis = action.payload;
    },
    setRecommendations: (
      state,
      action: PayloadAction<ShapeOfYouState["recommendations"]>
    ) => {
      state.recommendations = action.payload;
    },
    setCapturedImage: (
      state,
      action: PayloadAction<ShapeOfYouState["capturedImage"]>
    ) => {
      state.capturedImage = action.payload;
    },
    setVirtualTryOnImage: (
      state,
      action: PayloadAction<ShapeOfYouState["virtualTryOnImage"]>
    ) => {
      state.virtualTryOnImage = action.payload;
    },
    clearShapeOfYouData: (state) => {
      state.faceAnalysis = null;
      state.recommendations = null;
      state.capturedImage = null;
      state.virtualTryOnImage = null;
      state.product = null;
      state.selectedPrice = null;
      state.media = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProductForShapeOfYou.fulfilled, (state, action) => {
      state.product = action.payload;
      state.selectedPrice =
        action.payload.prices?.find(
          (v: any) => v.id == action.payload.defaultPriceId
        ) || null;
      // Use medias (plural) from Product model, fallback to media (singular) if it exists
      state.media = action.payload.medias || action.payload.media || null;
    });
  },
});

export const shapeOfYouActions = shapeOfYouSlice.actions;

export const selectShapeOfYou = (state: AppState) => state.shapeOfYou;

export default shapeOfYouSlice.reducer;
